/* <gr-product-gallery> — native swipe track + thumbnail navigation.

   Touch devices keep the browser's own swipe physics. JavaScript only updates
   the selected thumbnail when the visible slide changes, and thumbnail-led
   movement stays locked to its requested image until scrolling completes. */

class GrProductGallery extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onMainScroll = this.onMainScroll.bind(this);
    this.onUserInteraction = this.onUserInteraction.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    if (this.mainTrack) {
      this.mainTrack.addEventListener('scroll', this.onMainScroll, { passive: true });
      this.mainTrack.addEventListener('pointerdown', this.onUserInteraction, { passive: true });
      this.mainTrack.addEventListener('wheel', this.onUserInteraction, { passive: true });
    }
    document.addEventListener('gr:variant:change', this.onVariantChange);

    this._activeIndex = this.thumbs.findIndex(function (thumb) {
      return thumb.getAttribute('aria-selected') === 'true';
    });

    requestAnimationFrame(() => {
      const selected = this.thumbs.find(function (thumb) {
        return thumb.getAttribute('aria-selected') === 'true';
      });
      if (selected) this.select(selected, { behavior: 'auto', scrollThumb: false });
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    if (this.mainTrack) {
      this.mainTrack.removeEventListener('scroll', this.onMainScroll);
      this.mainTrack.removeEventListener('pointerdown', this.onUserInteraction);
      this.mainTrack.removeEventListener('wheel', this.onUserInteraction);
    }
    document.removeEventListener('gr:variant:change', this.onVariantChange);
    if (this._scrollFrame) cancelAnimationFrame(this._scrollFrame);
    if (this._scrollEndTimer) window.clearTimeout(this._scrollEndTimer);
    if (this._programmaticTimer) window.clearTimeout(this._programmaticTimer);
    this._bound = false;
  }

  get thumbs() { return Array.from(this.querySelectorAll('[data-gallery-thumb]')); }
  get slides() { return Array.from(this.querySelectorAll('[data-gallery-slide]')); }
  get mainTrack() { return this.querySelector('[data-gallery-main]'); }
  get reducedMotion() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }

  onClick(event) {
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (!thumb || !this.contains(thumb)) return;
    this.select(thumb);
  }

  onKeydown(event) {
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (!thumb || !this.contains(thumb)) return;

    const thumbs = this.thumbs;
    const index = thumbs.indexOf(thumb);
    if (index === -1) return;

    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = thumbs[(index + 1) % thumbs.length];
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = thumbs[(index - 1 + thumbs.length) % thumbs.length];
    else if (event.key === 'Home') next = thumbs[0];
    else if (event.key === 'End') next = thumbs[thumbs.length - 1];
    if (!next) return;

    event.preventDefault();
    this.select(next);
    next.focus();
  }

  onMainScroll() {
    if (this._scrollFrame) return;
    this._scrollFrame = requestAnimationFrame(() => {
      this._scrollFrame = null;
      const track = this.mainTrack;
      const slides = this.slides;
      if (!track || !slides.length) return;

      if (this._programmaticIndex !== undefined) {
        const target = slides[this._programmaticIndex];
        if (target && Math.abs(target.offsetLeft - track.scrollLeft) <= 1) {
          this.finishProgrammaticScroll();
        }
        return;
      }

      let closestIndex = 0;
      let closestDistance = Infinity;
      slides.forEach(function (slide, index) {
        const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      this.setActiveIndex(closestIndex, { scrollThumb: false });
      this.queueThumbReveal();
    });
  }

  onUserInteraction() {
    if (this._programmaticIndex === undefined) return;
    if (this._programmaticTimer) window.clearTimeout(this._programmaticTimer);
    this._programmaticTimer = null;
    this._programmaticIndex = undefined;
  }

  onVariantChange(event) {
    const variant = event.detail && event.detail.variant;
    const mediaId = variant && variant.featured_media_id;
    if (!mediaId) return;

    const match = this.thumbs.find(function (thumb) {
      return String(thumb.getAttribute('data-media-id')) === String(mediaId);
    });
    if (match) this.select(match);
  }

  select(thumb, options) {
    const thumbs = this.thumbs;
    const slides = this.slides;
    const index = thumbs.indexOf(thumb);
    if (index === -1 || !slides[index] || !this.mainTrack) return;

    const settings = options || {};
    const behavior = settings.behavior || (this.reducedMotion ? 'auto' : 'smooth');
    this.setActiveIndex(index, { scrollThumb: settings.scrollThumb !== false });

    if (Math.abs(slides[index].offsetLeft - this.mainTrack.scrollLeft) <= 1) {
      this.finishProgrammaticScroll();
      return;
    }

    if (behavior === 'smooth') {
      this._programmaticIndex = index;
      if (this._programmaticTimer) window.clearTimeout(this._programmaticTimer);
      this._programmaticTimer = window.setTimeout(() => {
        this._programmaticTimer = null;
        this._programmaticIndex = undefined;
        this.syncFromTrack();
      }, 1200);
    } else {
      this._programmaticIndex = undefined;
    }

    this.mainTrack.scrollTo({
      left: slides[index].offsetLeft,
      behavior: behavior
    });
  }

  setActiveIndex(index, options) {
    const thumbs = this.thumbs;
    const slides = this.slides;
    if (!thumbs[index] || !slides[index]) return;

    if (this._activeIndex === index) return;
    this._activeIndex = index;

    thumbs.forEach(function (thumb, thumbIndex) {
      const selected = thumbIndex === index;
      thumb.setAttribute('aria-selected', selected ? 'true' : 'false');
      thumb.tabIndex = selected ? 0 : -1;
    });
    slides.forEach(function (slide, slideIndex) {
      slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
    });

    if (!options || options.scrollThumb !== false) this.revealThumb(thumbs[index]);
  }

  syncFromTrack() {
    const track = this.mainTrack;
    const slides = this.slides;
    if (!track || !slides.length) return;

    let closestIndex = 0;
    let closestDistance = Infinity;
    slides.forEach(function (slide, index) {
      const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    this.setActiveIndex(closestIndex, { scrollThumb: false });
    this.revealThumb(this.thumbs[closestIndex]);
  }

  queueThumbReveal() {
    if (this._scrollEndTimer) window.clearTimeout(this._scrollEndTimer);
    this._scrollEndTimer = window.setTimeout(() => {
      this._scrollEndTimer = null;
      const thumb = this.thumbs[this._activeIndex];
      if (thumb) this.revealThumb(thumb);
    }, 100);
  }

  finishProgrammaticScroll() {
    const index = this._programmaticIndex;
    if (this._programmaticTimer) window.clearTimeout(this._programmaticTimer);
    this._programmaticTimer = null;
    this._programmaticIndex = undefined;
    const thumb = this.thumbs[index === undefined ? this._activeIndex : index];
    if (thumb) this.revealThumb(thumb);
  }

  revealThumb(thumb) {
    const strip = thumb.parentElement;
    if (!strip) return;

    const maxScroll = Math.max(0, strip.scrollWidth - strip.clientWidth);
    const target = Math.max(0, Math.min(maxScroll, thumb.offsetLeft + (thumb.offsetWidth / 2) - (strip.clientWidth / 2)));
    if (Math.abs(strip.scrollLeft - target) < 1) return;

    strip.scrollTo({ left: target, behavior: this.reducedMotion ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('gr-product-gallery')) customElements.define('gr-product-gallery', GrProductGallery);
